import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { WarehouseService } from './apps/warehouse/warehouse.service';
import { CoreService } from './core.service';

@Injectable({
  providedIn: 'root'
})
export class WarehouseAccessService {

  // ============================================================
  // WAREHOUSES CACHE
  // ============================================================

  private warehousesSubject = new BehaviorSubject<any[]>([]);

  public warehouses$ = this.warehousesSubject.asObservable();

  private loaded = false;

  // ============================================================
  // SELECTED WAREHOUSE
  // ============================================================

  private selectedWarehouseSubject =
    new BehaviorSubject<number | null>(null);

  public selectedWarehouse$ =
    this.selectedWarehouseSubject.asObservable();

  constructor(
    private warehouseService: WarehouseService,
    private core: CoreService
  ) {}

  // ============================================================
  // LOAD WAREHOUSES
  // Solo llama la API la primera vez
  // ============================================================

  loadWarehouses(force = false): Observable<any[]> {

    // Ya están cargados -> devolver cache
    if (this.loaded && !force) {
      return of(this.warehousesSubject.value);
    }

    return this.warehouseService.getWarehouses().pipe(

      tap((warehouses: any[]) => {

        const list = warehouses || [];

        // Guardar en memoria
        this.warehousesSubject.next(list);

        this.loaded = true;

        // Si solamente tiene un warehouse,
        // seleccionarlo automáticamente
        if (
          list.length === 1 &&
          this.selectedWarehouseSubject.value === null
        ) {
          this.setSelectedWarehouse(
            Number(list[0].id)
          );
        }
      })
    );
  }

  // ============================================================
  // GET CACHED WAREHOUSES
  // ============================================================

  get warehouses(): any[] {
    return this.warehousesSubject.value;
  }

  // ============================================================
  // SELECT WAREHOUSE
  // ============================================================

  setSelectedWarehouse(
    warehouseId: number | null
  ): void {

    if (warehouseId === null) {
      this.selectedWarehouseSubject.next(null);
      return;
    }

    const id = Number(warehouseId);

    if (!Number.isFinite(id) || id <= 0) {
      console.warn(
        'Invalid warehouse id:',
        warehouseId
      );

      return;
    }

    this.selectedWarehouseSubject.next(id);
  }

  // ============================================================
  // GET SELECTED WAREHOUSE ID
  // ============================================================

  get selectedWarehouseId(): number | null {
    return this.selectedWarehouseSubject.value;
  }

  // ============================================================
  // GET SELECTED WAREHOUSE OBJECT
  // ============================================================

  get selectedWarehouse(): any | null {

    const id = this.selectedWarehouseId;

    if (!id) {
      return null;
    }

    return this.getWarehouse(id);
  }

  // ============================================================
  // GET WAREHOUSE BY ID
  // ============================================================

  getWarehouse(
    warehouseId: number | string
  ): any | null {

    const id = Number(warehouseId);

    if (!Number.isFinite(id)) {
      return null;
    }

    return (
      this.warehousesSubject.value.find(
        warehouse =>
          Number(warehouse.id) === id
      ) ?? null
    );
  }

  // ============================================================
  // CHECK ACCESS TO WAREHOUSE
  // Frontend convenience only.
  // Backend must still validate security.
  // ============================================================

  hasWarehouseAccess(
    warehouseId: number | string
  ): boolean {

    const id = Number(warehouseId);

    return this.warehousesSubject.value.some(
      warehouse =>
        Number(warehouse.id) === id
    );
  }

  // ============================================================
  // CURRENT ROLE
  // ============================================================

  get role(): string {

    return this.core.getRole() ?? '';
  }

  // ============================================================
  // ADMIN / OWNER
  // ============================================================

  get isAdmin(): boolean {

    return (
      this.role === 'Admin' ||
      this.role === 'CompanyOwner'
    );
  }

  // ============================================================
  // CAN SELECT WAREHOUSE
  // ============================================================

  get canSelectWarehouse(): boolean {

    return [
      'Admin',
      'CompanyOwner',
      'Manager',
      'Assistant'
    ].includes(this.role);
  }

  // ============================================================
  // MANAGER
  // ============================================================

  get isManager(): boolean {
    return this.role === 'Manager';
  }

  // ============================================================
  // ASSISTANT
  // ============================================================

  get isAssistant(): boolean {
    return this.role === 'Assistant';
  }

  // ============================================================
  // REFRESH WAREHOUSES
  // Fuerza nueva llamada al backend
  // ============================================================

  refresh(): Observable<any[]> {

    return this.loadWarehouses(true);
  }

  // ============================================================
  // CLEAR CACHE
  // Usar cuando el usuario haga logout
  // ============================================================

  clear(): void {

    this.loaded = false;

    this.warehousesSubject.next([]);

    this.selectedWarehouseSubject.next(null);
  }
}